import type { ContractFormData } from "./contractSchema";
import type { SaldoFormData } from "./saldoSchema";
import { parseBRL } from "./masks";
import type { PreviewResponse } from "../types/preview";
import type { ApiErrorCode, CheckoutBuyer, CheckoutSession, CheckoutStatus, ReleasedDocument } from "../types/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_TIMEOUT_MS = 12_000;

const SERVER_CODES: ApiErrorCode[] = [
  "DEADLINE_EXPIRED", "DEADLINE_FUTURE", "RATE_LIMITED", "VALIDATION_ERROR", "PREVIEW_EXPIRED", "PRICE_MISMATCH",
  "DATA_MISMATCH", "ALREADY_PAID", "PAYMENT_REQUIRED", "PAYMENT_UNAVAILABLE", "PROVIDER_ERROR", "FORBIDDEN", "NOT_FOUND",
];

const STATUS_CODES: Record<number, ApiErrorCode> = {
  402: "PAYMENT_REQUIRED", 403: "FORBIDDEN", 404: "NOT_FOUND", 409: "ALREADY_PAID", 410: "PREVIEW_EXPIRED",
  429: "RATE_LIMITED", 502: "PROVIDER_ERROR", 503: "PAYMENT_UNAVAILABLE",
};

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly days?: number;

  constructor(code: ApiErrorCode, message: string, days?: number) {
    super(message);
    this.code = code;
    this.days = days;
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

function toApiError(status: number, body: unknown): ApiError {
  const detail = isRecord(body) && isRecord(body.detail) ? body.detail : {};
  const code = SERVER_CODES.find((c) => c === detail.code) ?? STATUS_CODES[status] ?? "UNKNOWN";
  const message = typeof detail.message === "string" ? detail.message : "Não foi possível concluir a operação.";
  return new ApiError(code, message, typeof detail.diasDecorridos === "number" ? detail.diasDecorridos : undefined);
}

async function request<T>(path: string, init: RequestInit, guard: (v: unknown) => v is T): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_URL}${path}`, { ...init, signal: controller.signal });
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) throw toApiError(res.status, body);
    if (!guard(body)) throw new ApiError("UNKNOWN", "Resposta inesperada do servidor.");
    return body;
  } catch (err: unknown) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === "AbortError") throw new ApiError("TIMEOUT", "Tempo de resposta excedido.");
    throw new ApiError("NETWORK", "Falha de conexão com o servidor.");
  } finally {
    window.clearTimeout(timer);
  }
}

const postJson = (payload: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
});

const isPreviewResponse = (v: unknown): v is PreviewResponse =>
  isRecord(v) && typeof v.protocolo === "string" && typeof v.diasDecorridos === "number" && isRecord(v.notificada) &&
  isRecord(v.notificante) && Array.isArray(v.emails) && Array.isArray(v.paragrafosOfuscados);

const isCheckoutSession = (v: unknown): v is CheckoutSession =>
  isRecord(v) && typeof v.paymentId === "string" && typeof v.qrCodeBase64 === "string" && typeof v.copiaECola === "string" &&
  typeof v.qrCodeImage === "string" && typeof v.gateway === "string" && typeof v.expiresAt === "string" && typeof v.releaseToken === "string" && typeof v.mode === "string";

const isCheckoutStatus = (v: unknown): v is CheckoutStatus =>
  isRecord(v) && (v.status === "PENDING" || v.status === "PAID" || v.status === "EXPIRED");

const isReleasedDocument = (v: unknown): v is ReleasedDocument =>
  isRecord(v) && typeof v.protocolo === "string" && Array.isArray(v.paragrafos) && typeof v.autenticadoEm === "string";

export function generatePreview(data: ContractFormData, days: number): Promise<PreviewResponse> {
  return request("/api/generate-preview", postJson({
    nomeCompleto: data.nomeCompleto.trim(),
    cpf: data.cpf,
    email: data.email.trim(),
    whatsapp: data.whatsapp,
    incorporadora: data.incorporadora,
    incorporadoraOutra: data.incorporadora === "Outra" ? data.incorporadoraOutra.trim() : "",
    dataAssinatura: data.dataAssinatura,
    valorEntrada: parseBRL(data.valorEntrada),
    formaPagamento: data.formaPagamento,
    diasDecorridos: days,
  }), isPreviewResponse);
}

export function generateSaldoPreview(data: SaldoFormData): Promise<PreviewResponse> {
  return request("/api/generate-preview", postJson({
    niche: "saldo-retido",
    instituicao: data.instituicao,
    instituicaoOutra: data.instituicao === "Outro" ? data.instituicaoOutra.trim() : "",
    valorBloqueado: parseBRL(data.valorBloqueado),
    dataBloqueio: data.dataBloqueio,
    motivoAlegado: data.motivoAlegado.trim(),
    identificadorConta: data.identificadorConta.trim(),
    tipoTitular: data.tipoTitular,
    razaoSocialOuNome: data.razaoSocialOuNome.trim(),
    documentoTitular: data.documentoTitular,
    email: data.email.trim(),
    whatsapp: data.whatsapp,
  }), isPreviewResponse);
}

export function checkoutPix(protocolo: string, buyer: CheckoutBuyer, valor: number): Promise<CheckoutSession> {
  return request("/api/checkout/pix", postJson({ protocolo, ...buyer, valor }), isCheckoutSession);
}

export function getCheckoutStatus(paymentId: string): Promise<CheckoutStatus> {
  return request(`/api/checkout/status/${encodeURIComponent(paymentId)}`, { method: "GET" }, isCheckoutStatus);
}

export function simulatePayment(paymentId: string): Promise<CheckoutStatus> {
  return request(`/api/checkout/simulate/${encodeURIComponent(paymentId)}`, { method: "POST" }, isCheckoutStatus);
}

export function fetchDocument(protocolo: string, releaseToken: string): Promise<ReleasedDocument> {
  return request(`/api/document/${encodeURIComponent(protocolo)}`, { method: "GET", headers: { "X-Release-Token": releaseToken } }, isReleasedDocument);
}
